import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import WhatsAppButton from '@/components/layout/WhatsAppButton';
import Hero from '@/components/sections/Hero';
import ServicesSection from '@/components/sections/ServicesSection';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import FAQSection from '@/components/sections/FAQSection';
import ContactForm from '@/components/sections/ContactForm';
import QuoteForm from '@/components/sections/QuoteForm';
import { SEO_PAGES, SEO_INTERNAL_LINKS } from '@/data/seoPages';
import { SITE_CONFIG } from '@/data/siteData';
import { MapPin, ArrowRight, CheckCircle } from 'lucide-react';

// Process Section Component (reused from homepage style)
const ProcessSection = () => {
  const { t } = useLanguage();
  
  const steps = [
    {
      number: "01",
      title: t("Prise de Contact", "Contact"),
      description: t("Contactez-nous par téléphone ou formulaire pour nous décrire vos besoins.", "Contact us by phone or form to describe your needs.")
    },
    {
      number: "02", 
      title: t("Devis Gratuit", "Free Quote"),
      description: t("Nous vous envoyons un devis détaillé sous 24h, sans engagement.", "We send you a detailed quote within 24h, no commitment.")
    },
    {
      number: "03",
      title: t("Intervention", "Service"),
      description: t("Notre équipe intervient selon le planning convenu avec professionnalisme.", "Our team intervenes according to the agreed schedule.")
    },
    {
      number: "04",
      title: t("Satisfaction", "Satisfaction"),
      description: t("Vous profitez d'un intérieur impeccable. 100% satisfaction garantie.", "You enjoy an impeccable interior. 100% satisfaction guaranteed.")
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-4">
            {t("Notre Processus", "Our Process")}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t("Comment Ça Marche", "How It Works")}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] mx-auto mt-6 rounded-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-[#1a365d] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-[#D4AF37] font-bold text-xl">{step.number}</span>
              </div>
              <h3 className="text-xl font-bold text-[#1a365d] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {step.title}
              </h3>
              <p className="text-slate-600">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// SEO Content Section
const SEOContentSection = ({ content, areas, city }) => {
  const { t } = useLanguage();

  return (
    <section className="py-20 bg-[#faf8f5]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* SEO Content */}
        <div 
          className="prose prose-slate prose-lg max-w-none prose-headings:text-[#1a365d] prose-headings:font-bold prose-a:text-[#D4AF37] prose-strong:text-[#1a365d]"
          style={{ fontFamily: 'inherit' }}
          dangerouslySetInnerHTML={{ __html: content }}
        />

        {/* Areas Covered */}
        {areas && areas.length > 0 && (
          <div className="mt-12 p-6 bg-white rounded-2xl shadow-sm border border-stone-100">
            <h3 className="text-xl font-bold text-[#1a365d] mb-4 flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <MapPin className="w-5 h-5 text-[#D4AF37]" />
              {t("Quartiers Desservis", "Areas Covered")}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {areas.map((area, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  <span>{area}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Internal Links Section
const InternalLinksSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-16 bg-white border-t border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-[#1a365d] mb-8 text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {t("Nos services de nettoyage sur la Côte d'Azur", "Our cleaning services on the French Riviera")}
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          {SEO_INTERNAL_LINKS.map((link, index) => (
            <Link
              key={index}
              to={link.href}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#faf8f5] hover:bg-[#1a365d] text-[#1a365d] hover:text-white rounded-full transition-all duration-300 border border-stone-200 hover:border-[#1a365d]"
            >
              {link.label}
              <ArrowRight className="w-4 h-4" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main SEO Page Component
const SEOPage = () => {
  const { t } = useLanguage();
  
  // Get slug from URL path directly
  const path = window.location.pathname;
  const slug = path.replace(/^\//, '');
  
  // Find the page data
  const pageData = SEO_PAGES.find(p => p.slug === slug);
  
  // Determine language from page data or URL
  const isEnglish = pageData?.language === 'en' || slug.startsWith('airbnb-cleaning');

  if (!pageData) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#1a365d] mb-4">
              {isEnglish ? 'Page not found' : 'Page non trouvée'}
            </h1>
            <Link to="/" className="text-[#D4AF37] hover:underline">
              {isEnglish ? 'Back to home' : "Retour à l'accueil"}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Service Schema for Airbnb pages, LocalBusiness for others
  const schemaData = pageData.serviceType?.includes('Airbnb') || pageData.serviceType?.includes('airbnb') 
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": isEnglish ? "Airbnb Cleaning Service" : "Service de Nettoyage Airbnb",
        "description": pageData.description,
        "provider": {
          "@type": "LocalBusiness",
          "name": "VS Riviera Conciergerie",
          "telephone": SITE_CONFIG.phone,
          "email": SITE_CONFIG.email
        },
        "serviceType": isEnglish ? "Airbnb Cleaning Service" : "Service de Nettoyage Airbnb",
        "areaServed": ["Fréjus", "Saint-Raphaël", "Sainte-Maxime", "Agay", "Saint-Aygulf"]
      }
    : {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "VS Riviera Conciergerie",
        "description": pageData.description,
        "telephone": SITE_CONFIG.phone,
        "email": SITE_CONFIG.email,
        "serviceType": "Nettoyage et ménage",
        "areaServed": ["Fréjus", "Saint-Raphaël", "Sainte-Maxime"],
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "Var",
          "addressCountry": "FR"
        }
      };

  return (
    <>
      <Helmet>
        <html lang={isEnglish ? "en" : "fr"} />
        <title>{pageData.title}</title>
        <meta name="description" content={pageData.description} />
        <meta property="og:title" content={pageData.title} />
        <meta property="og:description" content={pageData.description} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/${pageData.slug}`} />
        <link rel="alternate" hreflang="fr" href={`${window.location.origin}/${pageData.slug}`} />
        <link rel="alternate" hreflang="en" href={`${window.location.origin}/en`} />
        <link rel="alternate" hreflang="x-default" href={`${window.location.origin}/${pageData.slug}`} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <Header />
      
      <main data-testid={`seo-page-${slug}`}>
        {/* Hero with custom H1 */}
        <Hero 
          subtitle="VS RIVIERA CONCIERGERIE"
          title={pageData.h1}
          description={pageData.description}
          cityName={pageData.city !== "Côte d'Azur" ? pageData.city : null}
        />

        {/* Services Section */}
        <ServicesSection />

        {/* Quote Form */}
        <QuoteForm />

        {/* Process Section */}
        <ProcessSection />

        {/* Testimonials */}
        <TestimonialsSection />

        {/* Contact Form */}
        <ContactForm />

        {/* SEO Content Block */}
        <SEOContentSection 
          content={pageData.content} 
          areas={pageData.areas}
          city={pageData.city}
        />

        {/* Internal Links */}
        <InternalLinksSection />

        {/* FAQ */}
        <FAQSection />
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default SEOPage;
